import {
  Campaign,
  CampaignStatusesEnum,
  CampaignStatusMessages,
} from '@/client/campaigns';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { useCampaign } from '@/hooks/query';
import { getCampaignRangeDate } from '@/utils/helpers';
import moment from 'moment';
import { Skeleton } from 'primereact/skeleton';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const StyledSkeleton = styled(Skeleton)`
  background-color: var(--gray-opacity-light);
`;

const ScheduledBanner = styled.div`
  width: 100%;
  background-color: var(--yellow-pale);
  border-radius: var(--xsmall-border-radius);
  border: 1px solid var(--yellow-dark-22);
  padding: var(--default-padding);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--default-padding);
  margin-top: var(--default-padding);
  cursor: pointer;

  .banner-info {
    display: flex;
    flex-direction: column;
    gap: var(--xsmall-padding);
    min-width: 0;
  }

  .status-badge {
    width: fit-content;
    background-color: var(--white-main);
    border-radius: var(--grand-border-radius);
    padding: var(--xxsmall-padding) var(--small-padding);
    font-size: var(--xsmall-font-size);
    line-height: var(--xsmall-line-height);
    font-weight: 500;
    color: var(--yellow-dark);
  }

  .campaign-name {
    font-size: var(--heading-small-font-size);
    line-height: 140%;
    font-weight: 700;
    color: var(--yellow-dark);
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .starts-in-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--xxsmall-padding);
    flex-shrink: 0;
    color: var(--black-main);
  }

  .calendar-icon {
    color: var(--yellow-dark);
  }

  .starts-in-text {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .starts-in-label {
    font-size: var(--xsmall-font-size);
    line-height: var(--xsmall-line-height);
    font-weight: 400;
  }

  .starts-in-value {
    font-size: var(--semi-big-font-size);
    line-height: var(--semi-big-line-height);
    font-weight: 700;
    white-space: nowrap;
  }

  @media (max-width: 899px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const ScheduledCampaignBanner: React.FC<{
  initialCampaign: Campaign;
}> = ({ initialCampaign }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLoading, campaign } = useCampaign(initialCampaign.id, {
    keepPreviousData: true,
  });

  if (isLoading || !campaign) {
    return (
      <ScheduledBanner style={{ cursor: 'default' }} aria-disabled={true}>
        <div className="banner-info">
          <StyledSkeleton width="200px" height="24px" borderRadius="100px" />
          <StyledSkeleton width="320px" height="24px" borderRadius="4px" />
        </div>
        <div className="starts-in-container">
          <StyledSkeleton width="24px" height="24px" borderRadius="4px" />
          <StyledSkeleton width="120px" height="14px" borderRadius="4px" />
          <StyledSkeleton width="60px" height="22px" borderRadius="4px" />
        </div>
      </ScheduledBanner>
    );
  }

  const daysUntilLaunch = Math.max(
    0,
    moment(campaign.launchDate)
      .startOf('day')
      .diff(moment().startOf('day'), 'days'),
  );

  return (
    <ScheduledBanner
      onClick={() =>
        navigate(RedirectPaths[RedirectPathsEnum.CAMPAIGNS_REPORT](campaign.id))
      }
    >
      <div className="banner-info">
        <span className="status-badge">
          {t(CampaignStatusMessages[CampaignStatusesEnum.SCHEDULED])}
          {' \u00b7 '}
          {getCampaignRangeDate(campaign)}
        </span>
        <h3 className="campaign-name">{campaign.name}</h3>
      </div>
      <div className="starts-in-container">
        <i className="pi pi-calendar calendar-icon" style={{ fontSize: 24 }} />
        <div className="starts-in-text">
          <span className="starts-in-label">
            {t('dashboard.banner.startsIn')}
          </span>
          <span className="starts-in-value">
            {t('dashboard.banner.daysCount', { count: daysUntilLaunch })}
          </span>
        </div>
      </div>
    </ScheduledBanner>
  );
};
